/* eslint-disable no-unused-vars */
import clsx from 'clsx'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import {
  BORDER_WEEKLY_CELL,
  EVENT_VERTICAL_SPACING,
  MIN_HEIGHT_EVENT,
  MIN_HEIGHT_HIDE_DETAIL,
  MINUTES_PER_HOUR,
  TIME_FORMAT
} from '../../../constants'
import { getIconforEvent } from '../../../iconsV2'
import { IEvent } from '../../../types'
import { convertType, customDateFormat, isDisplayIconV2 } from '../../../utils'
import styles from '../SAPPCalendar.module.css'
import CustomTooltip from '../common/CustomTooltip'
import { useDeviceType } from 'src/hooks/use-device-type'

interface IWeeklyEventProps {
  event: IEvent
  onOpenDetail?: (date: Date, events: IEvent[]) => void
  onEventDetail?: (event: IEvent) => void
  zIndex: number
  isAllDay?: boolean
  spanDays?: number
  setEventDetail?: (params: {event: IEvent | null, date: Dayjs}) => void
  isOnDetail?: boolean
  date: Dayjs
  overlapCount?: number
  overlapIndex?: number
}

const calculateBoxSize = (
  startTime: Date,
  endTime: Date,
  isAllDay?: boolean
) => {
  const deviceType = useDeviceType()

  if (isAllDay) {
    return {
      top: 2,
      height: MIN_HEIGHT_HIDE_DETAIL
    }
  }

  const minutes = dayjs(startTime).minute()
  const diffMinutes = dayjs(endTime).diff(startTime, 'minute', true)

  const border = Math.floor(
    ((minutes + diffMinutes) / MINUTES_PER_HOUR) * BORDER_WEEKLY_CELL
  )

  let height: number
  if (deviceType === 'mobile') {
    height = diffMinutes * (28 / 60) + border
  } else if (deviceType === 'tablet') {
    height = diffMinutes * (40 / 60) + border
  } else {
    height = diffMinutes + border
  }

  return {
    top: minutes,
    height
  }
}

const WeeklyEvent = ({
  event,
  onOpenDetail,
  onEventDetail,
  zIndex,
  spanDays,
  isAllDay,
  setEventDetail,
  isOnDetail,
  date,
  overlapCount = 1,
  overlapIndex = 0

}: IWeeklyEventProps) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const [shouldTruncateTitle, setShouldTruncateTitle] = useState(false)
  const [shouldTruncateContent, setShouldTruncateContent] = useState(false)
  const deviceType = useDeviceType()
  const { top, height } = calculateBoxSize(
    event.startDate,
    event.endDate,
    event.isAllDay
  )

  useEffect(() => {
    if (contentRef.current) {
      let padding = 0
      let extraHeight = 0
      if (deviceType === 'mobile') {
        padding = 4 * 2
        extraHeight = 0
      } else if (deviceType === 'tablet') {
        padding = 8 * 2
        extraHeight = 24
      } else {
        padding = 12 * 2
        extraHeight = 0
      }
      const actualHeight = contentRef.current.offsetHeight
      const needsTruncation = actualHeight + padding + extraHeight > height

      if (needsTruncation) {
        setShouldTruncateTitle(true)
        if (!shouldTruncateTitle) return
        setShouldTruncateContent(true)
      }
    }
  }, [height, shouldTruncateTitle])

  const isMinHeight = height < MIN_HEIGHT_EVENT

  const formattedDate =
    customDateFormat(event.startDate, TIME_FORMAT) +
    ' - ' +
    customDateFormat(event.endDate, TIME_FORMAT)
  const iconList = getIconforEvent(event)

  const className = event?.isOverDue
    ? `event-error`
    : `event-${convertType[event.type] || 'default'}`

  const classNameBefore = event?.isOverDue
    ? `event__before-error`
    : `event__before-${convertType[event.type] || 'default'}`

  const classNameBorder = event?.isOverDue
    ? `event__border-error`
    : `event__border-${convertType[event.type] || 'default'}`

  // ---- Width responsive theo thiết bị ----
  const WEEKLY_CELL_WIDTH_DESKTOP = 100
  const WEEKLY_CELL_WIDTH_TABLET = '152px'
  const WEEKLY_CELL_WIDTH_MOBILE = '90px'

  let eventWidth = 'auto'
  const days = event.spanDays ?? 1

  if (isAllDay && days > 1) {
    if (deviceType === 'desktop') {
      eventWidth = `calc(${WEEKLY_CELL_WIDTH_DESKTOP * days}%)`
    } else if (deviceType === 'tablet') {
      eventWidth = `calc(${WEEKLY_CELL_WIDTH_TABLET} * ${days})`
    } else {
      eventWidth = `calc(${WEEKLY_CELL_WIDTH_MOBILE} * ${days})`
    }
  }

  const margin = 4;
  const gap = margin;
  const percent = 100 / overlapCount;
  const width = `calc(${percent}% - ${(gap * (overlapCount - 1)) / overlapCount}px - ${margin}px)`;
  const left = `calc(${percent * overlapIndex}% )`;

  return (
    <div
      className={clsx(
        styles[className],
        styles['event-weekly'],
        (isOnDetail) && styles[classNameBorder]
      )}
      onClick={() => {
        onOpenDetail && onOpenDetail(dayjs(event.startDate).toDate(), [event])
        onEventDetail && onEventDetail({...event, current_date: date})
        setEventDetail && setEventDetail({event: event, date: date})
      }}
        style={{
        position: 'absolute',
        top: `${top}px`,
        height: !isAllDay
          ? `${Math.max(height, MIN_HEIGHT_EVENT) - EVENT_VERTICAL_SPACING}px`
          : "60px",
        zIndex: isAllDay && days > 1 ? 2 : 1,
        width: isAllDay
          ? eventWidth
          :width,
        left: isAllDay
          ? undefined
          : left,
      }}
    >
      <span
        className={clsx(
          styles['weekly-event__before'],
          styles[classNameBefore]
        )}
      />
      <div
        className={clsx(
          styles['weekly-event'],
          isMinHeight && styles['event--min-height']
        )}
      >
        {isDisplayIconV2(event) && (
          <div className={styles['weekly-event__title_icon']}>{iconList}</div>
        )}
        <div
          ref={contentRef}
          className={clsx(
            styles['weekly-event__content'],
            height < MIN_HEIGHT_HIDE_DETAIL &&
              styles['weekly-event--collapse']
          )}
        >
          <div
            className={clsx(
              styles['weekly-event__title'],
              shouldTruncateTitle && styles['weekly-event__title--truncate']
            )}
          >
            <CustomTooltip title={event.title}>{event.title}</CustomTooltip>
          </div>
          <div
            className={clsx(
              styles['weekly-event__date'],
              shouldTruncateContent && styles['weekly-event__date--truncate']
            )}
          >
            <CustomTooltip title={formattedDate}>
              {formattedDate}
            </CustomTooltip>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WeeklyEvent
